/**
 * Catalog Browser Popover
 * Three-level cascading selection: Application -> Schema -> Table (-> Columns for mask mode)
 * Uses data from the catalog-summary endpoint
 */

import { useState, useMemo } from 'react'
import { Database, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import { useCatalogSummary } from '@/hooks/use-data-tables'
import type {
  CatalogApplication,
  CatalogSchema,
  CatalogTable,
  CatalogTableSelection,
  CatalogColumnSelection,
} from '@/types/data-table'

type BrowseStep = 'application' | 'schema' | 'table' | 'column'

interface CatalogBrowserPopoverProps {
  mode: 'table' | 'column'
  onSelectTable?: (selection: CatalogTableSelection) => void
  onSelectColumns?: (selection: CatalogColumnSelection) => void
  selectedTableId?: string
}

export function CatalogBrowserPopover({
  mode,
  onSelectTable,
  onSelectColumns,
  selectedTableId,
}: CatalogBrowserPopoverProps) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<BrowseStep>('application')
  const [selectedApp, setSelectedApp] = useState<CatalogApplication | null>(null)
  const [selectedSchema, setSelectedSchema] = useState<CatalogSchema | null>(null)
  const [selectedTable, setSelectedTable] = useState<CatalogTable | null>(null)
  const [checkedColumns, setCheckedColumns] = useState<Set<string>>(new Set())

  const { data: catalog, isLoading } = useCatalogSummary()

  const resetState = () => {
    setStep('application')
    setSelectedApp(null)
    setSelectedSchema(null)
    setSelectedTable(null)
    setCheckedColumns(new Set())
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      resetState()
    }
  }

  const handleSelectApp = (app: CatalogApplication) => {
    setSelectedApp(app)
    if (app.schemas.length === 1) {
      setSelectedSchema(app.schemas[0])
      setStep('table')
    } else {
      setStep('schema')
    }
  }

  const handleSelectSchema = (schema: CatalogSchema) => {
    setSelectedSchema(schema)
    setStep('table')
  }

  const handleSelectTable = (table: CatalogTable) => {
    if (!selectedApp || !selectedSchema) return

    if (mode === 'table') {
      onSelectTable?.({
        tableId: table.id,
        tableName: table.tableName,
        schemaName: selectedSchema.name,
        applicationName: selectedApp.displayName ?? selectedApp.name,
      })
      setOpen(false)
      resetState()
    } else {
      setSelectedTable(table)
      setCheckedColumns(new Set())
      setStep('column')
    }
  }

  const handleToggleColumn = (columnName: string) => {
    setCheckedColumns((prev) => {
      const next = new Set(prev)
      if (next.has(columnName)) {
        next.delete(columnName)
      } else {
        next.add(columnName)
      }
      return next
    })
  }

  const handleConfirmColumns = () => {
    if (!selectedApp || !selectedSchema || !selectedTable) return
    if (checkedColumns.size === 0) return

    onSelectColumns?.({
      tableId: selectedTable.id,
      tableName: selectedTable.tableName,
      schemaName: selectedSchema.name,
      applicationName: selectedApp.displayName ?? selectedApp.name,
      columns: Array.from(checkedColumns),
    })
    setOpen(false)
    resetState()
  }

  const handleBack = () => {
    if (step === 'column') {
      setStep('table')
      setSelectedTable(null)
      setCheckedColumns(new Set())
    } else if (step === 'table') {
      if (selectedApp && selectedApp.schemas.length === 1) {
        setStep('application')
        setSelectedApp(null)
        setSelectedSchema(null)
      } else {
        setStep('schema')
        setSelectedSchema(null)
      }
    } else if (step === 'schema') {
      setStep('application')
      setSelectedApp(null)
    }
  }

  const breadcrumb = useMemo(() => {
    const parts: string[] = []
    if (selectedApp) parts.push(selectedApp.displayName ?? selectedApp.name)
    if (selectedSchema) parts.push(selectedSchema.name)
    if (selectedTable) parts.push(selectedTable.tableName)
    return parts.join(' > ')
  }, [selectedApp, selectedSchema, selectedTable])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5">
          <Database className="h-3.5 w-3.5" />
          Browse Catalog
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        {isLoading ? (
          <div className="flex items-center justify-center p-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !catalog || catalog.applications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No catalog data available. Run a Discovery scan first.
          </div>
        ) : (
          <Command>
            {step !== 'application' && (
              <div className="flex items-center gap-1 border-b px-2 py-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={handleBack}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs text-muted-foreground truncate">{breadcrumb}</span>
              </div>
            )}
            <CommandInput placeholder={getPlaceholder(step)} />
            <CommandList className="max-h-64">
              <CommandEmpty>No results found.</CommandEmpty>

              {step === 'application' && (
                <ApplicationList
                  applications={catalog.applications}
                  onSelect={handleSelectApp}
                />
              )}

              {step === 'schema' && selectedApp && (
                <SchemaList
                  schemas={selectedApp.schemas}
                  onSelect={handleSelectSchema}
                />
              )}

              {step === 'table' && selectedSchema && (
                <TableList
                  tables={selectedSchema.tables}
                  onSelect={handleSelectTable}
                  selectedTableId={selectedTableId}
                />
              )}

              {step === 'column' && selectedTable && (
                <ColumnList
                  columns={selectedTable.columns}
                  checked={checkedColumns}
                  onToggle={handleToggleColumn}
                />
              )}
            </CommandList>

            {step === 'column' && (
              <div className="border-t p-2 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={checkedColumns.size === 0}
                  onClick={handleConfirmColumns}
                >
                  Select {checkedColumns.size} column{checkedColumns.size !== 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}

function getPlaceholder(step: BrowseStep): string {
  switch (step) {
    case 'application':
      return 'Search applications...'
    case 'schema':
      return 'Search schemas...'
    case 'table':
      return 'Search tables...'
    case 'column':
      return 'Search columns...'
  }
}

function ApplicationList({
  applications,
  onSelect,
}: {
  applications: CatalogApplication[]
  onSelect: (app: CatalogApplication) => void
}) {
  return (
    <CommandGroup heading="Applications">
      {applications.map((app) => (
        <CommandItem
          key={app.id}
          value={app.displayName ?? app.name}
          onSelect={() => onSelect(app)}
          className="flex items-center justify-between"
        >
          <span className="text-sm">{app.displayName ?? app.name}</span>
          <div className="flex items-center gap-1 text-muted-foreground">
            <span className="text-xs">{app.schemas.length} schema{app.schemas.length !== 1 ? 's' : ''}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  )
}

function SchemaList({
  schemas,
  onSelect,
}: {
  schemas: CatalogSchema[]
  onSelect: (schema: CatalogSchema) => void
}) {
  return (
    <CommandGroup heading="Schemas">
      {schemas.map((schema) => (
        <CommandItem
          key={schema.name}
          value={schema.name}
          onSelect={() => onSelect(schema)}
          className="flex items-center justify-between"
        >
          <span className="text-sm font-mono">{schema.name}</span>
          <div className="flex items-center gap-1 text-muted-foreground">
            <span className="text-xs">{schema.tables.length} table{schema.tables.length !== 1 ? 's' : ''}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  )
}

function TableList({
  tables,
  onSelect,
  selectedTableId,
}: {
  tables: CatalogTable[]
  onSelect: (table: CatalogTable) => void
  selectedTableId?: string
}) {
  return (
    <CommandGroup heading="Tables">
      {tables.map((table) => {
        const classifiedCount = table.columns.filter((c) => c.classification).length
        return (
          <CommandItem
            key={table.id}
            value={table.tableName}
            onSelect={() => onSelect(table)}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono">{table.tableName}</span>
              {table.id === selectedTableId && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">current</Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {classifiedCount > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                  {classifiedCount} classified
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">{table.columns.length} col</span>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </CommandItem>
        )
      })}
    </CommandGroup>
  )
}

function ColumnList({
  columns,
  checked,
  onToggle,
}: {
  columns: Array<{
    id: string
    columnName: string
    displayName: string | null
    dataType: string
    classification: { id: string; name: string; level: string; color: string | null } | null
  }>
  checked: Set<string>
  onToggle: (columnName: string) => void
}) {
  return (
    <CommandGroup heading="Columns">
      {columns.map((col) => (
        <CommandItem
          key={col.id}
          value={col.columnName}
          onSelect={() => onToggle(col.columnName)}
          className="flex items-center gap-2"
        >
          <Checkbox
            checked={checked.has(col.columnName)}
            className="h-3.5 w-3.5"
            tabIndex={-1}
          />
          <span className="text-sm font-mono flex-1">{col.columnName}</span>
          <span className="text-[10px] text-muted-foreground">{col.dataType}</span>
          {col.classification && (
            <Badge
              variant="outline"
              className="text-[10px] px-1 py-0"
              style={col.classification.color ? { borderColor: col.classification.color, color: col.classification.color } : undefined}
            >
              {col.classification.name}
            </Badge>
          )}
        </CommandItem>
      ))}
    </CommandGroup>
  )
}
